import { useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { text } from '../constants/translations';

function useGroups(userRole, language) {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    const [groups, setGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [createdGroups, setCreatedGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const fetchMemberOfGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/member-of-groups`, { method: "GET" });
            const data = await res.json();
            setGroups(data || []);
        } catch (err) {
            console.error('Error fetching my groups:', err);
            setError("Failed to load groups");
        } finally {
            setLoading(false);
        }
    };

    const fetchAllGroups = async () => {
        try {
            const res = await apiFetch(`${apiBaseUrl}/api/groups/`, { method: "GET" });
            const data = await res.json();
            setAllGroups(data || []);
        } catch (err) {
            console.error('Error fetching all groups:', err);
        }
    };

    const fetchCreatedGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/created-groups`, { method: "GET" });
            const data = await res.json();
            setCreatedGroups(data || []);
        } catch (err) {
            console.error('Error fetching created groups:', err);
            setError("Failed to load created groups");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (newGroupName, onSuccess) => {
        if (userRole !== "admin" && userRole !== "manager") {
            setError(text[language].unauthorized);
            return;
        }

        if (!newGroupName.trim()) {
            setError(text[language].groupNameIsRequred);
            return;
        }

        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ groupName: newGroupName })
            });

            if (!res.ok) throw new Error("Failed to create group");

            setSuccess(text[language].groupCreated);
            fetchMemberOfGroups();
            if (userRole === "admin" || userRole === "manager") {
                fetchAllGroups();
            }
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(text[language].failedToCreate);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignEmployees = async (selectedGroup, selectedEmployeeIds, onSuccess) => {
        if (userRole !== "admin" && userRole !== "manager") {
            setError(text[language].unauthorized);
            return;
        }

        if (!selectedGroup || selectedEmployeeIds.length === 0) {
            setError(text[language].atLeastOneEmployee);
            return;
        }

        try {
            setLoading(true);
            const empIds = selectedEmployeeIds.map(id => parseInt(id));

            const res = await apiFetch(`${apiBaseUrl}/api/groups/${selectedGroup.groupId}/members`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ empIds })
            });

            if (!res.ok) throw new Error("Failed to assign employees");

            setSuccess(text[language].employeesAssigned);
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(text[language].failedToAssign);
        } finally {
            setLoading(false);
        }
    };

    return {
        groups,
        allGroups,
        createdGroups,
        loading,
        error,
        success,
        setError,
        setSuccess,
        fetchMemberOfGroups,
        fetchAllGroups,
        fetchCreatedGroups,
        handleCreateGroup,
        handleAssignEmployees,
    };
}

export default useGroups;